/**
 * Logique de l'onglet Paliers : les paliers de développement financés par les
 * investisseurs associés (MVP, V1, V2, version finale) et les modules qu'ils
 * débloquent pour TOUTE la plateforme. Pure, donc testée sans écran.
 *
 * Distinct des interrupteurs d'urgence : un module peut être débloqué par son
 * palier ET coupé en urgence, ou l'inverse. Les deux axes ne se confondent pas.
 */
import { ApiError } from './api.js';
import { raisonErreur } from './modules.js';
import type { IdPalierDev, PalierDevVue } from '../types.js';

/** L'ordre d'affichage, et l'ordre des investissements. */
export const ORDRE_PALIERS: readonly IdPalierDev[] = ['mvp', 'v1', 'v2', 'finale'];

/** Inclus (MVP), tout débloqué, une partie seulement, ou rien. */
export type EtatPalier = 'inclus' | 'debloque' | 'partiel' | 'verrouille';

export function etatPalier(
  palier: Pick<PalierDevVue, 'verrouillable' | 'debloques' | 'total'>,
): EtatPalier {
  if (!palier.verrouillable) return 'inclus';
  if (palier.total > 0 && palier.debloques >= palier.total) return 'debloque';
  if (palier.debloques <= 0) return 'verrouille';
  return 'partiel';
}

/** Part débloquée, de 0 à 100, pour la jauge. */
export function pourcentage(palier: Pick<PalierDevVue, 'debloques' | 'total'>): number {
  if (palier.total <= 0) return 0;
  return Math.round((Math.min(palier.debloques, palier.total) / palier.total) * 100);
}

/**
 * L'action en un geste d'un palier : « tout débloquer » tant qu'il reste un
 * module verrouillé, « tout verrouiller » quand tout est débloqué.
 */
export function actionGroupee(
  palier: Pick<PalierDevVue, 'debloques' | 'total'>,
): 'debloquer' | 'verrouiller' {
  return palier.total > 0 && palier.debloques >= palier.total ? 'verrouiller' : 'debloquer';
}

/** Les paliers dans l'ordre d'affichage, quel que soit l'ordre de la réponse. */
export function paliersOrdonnes(paliers: readonly PalierDevVue[]): PalierDevVue[] {
  const rang = (id: IdPalierDev): number => ORDRE_PALIERS.indexOf(id);
  return [...paliers].sort((a, b) => rang(a.id) - rang(b.id));
}

/** 409 : les paliers ont bougé depuis la lecture (`revision_paliers_perimee`). */
export function estRevisionPerimee(erreur: unknown): boolean {
  return erreur instanceof ApiError && erreur.status === 409;
}

export type ErreurPalier = 'palier_non_verrouillable' | 'module_non_verrouillable' | 'introuvable';

/** Reconnaît les refus nommés du contrat ; le reste est une erreur générique. */
export function erreurPalier(erreur: unknown): ErreurPalier | null {
  if (!(erreur instanceof ApiError)) return null;
  if (erreur.status === 404) return 'introuvable';
  const raison = raisonErreur(erreur) ?? erreur.code;
  if (raison === 'palier_non_verrouillable' || raison === 'module_non_verrouillable') return raison;
  return null;
}
